import { Box, Skeleton, Stack, Typography } from "@mui/material";
import {
  Badge,
  TableColumn,
  types,
  ObjectMetadataTags,
  utils
} from "@vilocnv/allsetra-core";
import { Clock, DriverIcon, MileageIcon, MovingArrowLeft } from "assets/icons";
import { HiddenBadgeIcon } from "components/ui/icons";
import "moment/locale/nl";
import {
  OBJECT_RIDE_MODES,
  OBJECT_RIDE_MODES_COLOR,
  getObjectRideTotalTimeInMinutes,
  getSplittedAddress,
  getAddressDisplayInfo
} from "../helpers";
import moment from "moment";
import { round, compact } from "lodash";
import { Fragment } from "react";
import { IFeatures } from "../types";

//
// TABLE HEADERS
//

export function modifyIconUrl(url: string | undefined) {
  return url ? url.replace(/\/icons\/([^/]+)-/, "/icons/$1-ns-") : "";
}

export const ALL_OBJECTS_TABLE_COLUMNS = (
  translator: any,
  lang = "en",
  resolvedAddresses: any[] = [],
  resolvedAddressLoading: boolean = false
): TableColumn<any>[] => {
  moment.locale(lang);
  return [
    {
      name: translator("tableHeading.object", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => (
        <Stack gap={0.5} sx={{ pointerEvents: "none" }}>
          <Stack direction={"row"} gap={1} alignItems={"flex-start"}>
            <img
              src={modifyIconUrl(row.icon?.url)}
              alt="Icon"
              width="24px"
              height="24px"
            />
            <Box>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#1D1F2B"
                }}
              >
                {row.name || "N/A"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "400",
                  color: "#646D7B"
                }}
              >
                ID {row?.aNumber || "N/A"}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      ),
      wrap: true,
      sortable: true,
      sortField: "name"
    },
    // {
    //   name: translator("tableHeading.objectType", {
    //     ns: "tableHeadingsTranslation"
    //   }),
    //   selector: (row: any) => (
    //     <Stack gap={1} sx={{ pointerEvents: "none" }}>
    //       {row.objectTypeName || "N/A"}
    //     </Stack>
    //   ),
    //   sortable: true,
    //   sortField: "objectTypeName"
    // },
    {
      name: translator("tableHeading.state", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => (
        <ObjectMetadataTags
          translator={translator}
          object={row}
          direction={"row"}
          isInTable
        />
      ),
      width: "200px"
    },
    {
      name: translator("tableHeading.lastReportedDriver", {
        ns: "tableHeadingsTranslation"
      }),
      cell: (row: any) => (
        <Stack
          direction={"row"}
          gap={1}
          alignItems={"center"}
          sx={{ pointerEvents: "none" }}
        >
          {/* <DriverIcon /> */}
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#1D1F2B"
            }}
          >
            {row.lastReportedDriver ? `${row.lastReportedDriver}` : "N/A"}
          </Typography>
        </Stack>
      )
    },
    {
      name: translator("tableHeading.lastLocation", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => {
        // Find the resolved address for this object
        const resolvedAddress = resolvedAddresses.find(
          (addr) =>
            addr.latitude === row.location?.latitude &&
            addr.longitude === row.location?.longitude
        );

        // Use the resolved address if found, otherwise use the existing one
        const addressToDisplay =
          row.location?.resolvedAddress || resolvedAddress?.address;

        return resolvedAddressLoading ? (
          <>
            <Skeleton variant="text" width={100} height={24} />
            <Skeleton variant="text" width={100} height={24} />
          </>
        ) : (
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#1D1F2B",
              pointerEvents: "none"
            }}
          >
            {getSplittedAddress(0, addressToDisplay)}
            <br />
            {getSplittedAddress(1, addressToDisplay) +
              getSplittedAddress(2, addressToDisplay)}
          </Typography>
        );
      },
      wrap: true
    },
    {
      name: translator("tableHeading.updated", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => {
        return (
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#1D1F2B",
              pointerEvents: "none"
            }}
          >
            {moment(row.lastUpdated).fromNow()}
          </Typography>
        );
      },
      sortable: true,
      sortField: "lastUpdated"
    },
    {
      name: translator("tableHeading.installationDate", {
        ns: "tableHeadingsTranslation"
      }),
      selector: (row: any) => {
        return (
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#1D1F2B",
              pointerEvents: "none"
            }}
          >
            {row?.installationDate
              ? moment(row?.installationDate).format("YYYY-MM-DD HH:MM:SS")
              : "N/A"}
          </Typography>
        );
      },
      sortable: true,
      sortField: "installationDate"
    }
  ];
};

export const OBJECT_RIDES_TABLE_COLUMNS = (
  translator: any,
  showMovementRides: boolean = false,
  resolvedAddresses: any[] = [],
  resolvedAddressLoading: boolean = false
): TableColumn<types.IObjectRide>[] => [
  {
    name: translator("tableHeading.rideType", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: types.IObjectRide & { isCorrection: boolean }) => {
      if (row.isCorrection) {
        return (
          <Box
            sx={{
              backgroundColor: "#323946",
              border: "1px solid #76828F",
              borderRadius: "4px",
              padding: "4px 8px",
              color: "#FFFFFF",
              fontSize: "11px",
              fontWeight: 600
            }}
          >
            {translator(`rideMode.correction`)}
          </Box>
        );
      }

      return (
        <Stack spacing={2}>
          <Stack>
            {/* @ts-ignore */}
            <Badge
              colorScheme={OBJECT_RIDE_MODES_COLOR[row.rideMode] ?? "error"}
            >
              {/* @ts-ignore */}
              <div>{OBJECT_RIDE_MODES(translator)[row.rideMode] ?? "N/A"}</div>
            </Badge>
          </Stack>
        </Stack>
      );
    },
    sortable: true,
    wrap: true,
    width: "150px"
  },

  {
    name: translator("tableHeading.rideStart", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: any) => {
      const resolvedAddress = resolvedAddresses.find(
        (addr) =>
          addr.latitude === row.startLatitude &&
          addr.longitude === row.startLongitude
      );

      const addressToDisplay = resolvedAddress?.address;

      return (
        <Stack spacing={2}>
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <div>{utils.formatDate({ date: row?.startDate })}</div>
            {row?.startedWithoutIgnition && showMovementRides && (
              <MovingArrowLeft
                label={translator("common.movementRide") ?? "Movement ride"}
              />
            )}
          </Stack>
          {(() => {
            const addressInfo = getAddressDisplayInfo(
              row.startLatitude,
              row.startLongitude,
              addressToDisplay
            );

            if (addressInfo.shouldShow) {
              return (
                <div>
                  {resolvedAddressLoading ? (
                    <>
                      <Skeleton variant="text" width={100} height={18} />
                      <Skeleton variant="text" width={100} height={18} />
                    </>
                  ) : (
                    addressToDisplay || "-"
                  )}
                </div>
              );
            } else if (addressInfo.isHidden) {
              return (
                <Box mx={20}>
                  <Badge
                    colorScheme="blank"
                    icon={<HiddenBadgeIcon />}
                    textWeight="light"
                  >
                    {translator("typography.addressIsHidden")}
                  </Badge>
                </Box>
              );
            } else if (addressInfo.isUnknown) {
              return (
                <Box mx={20}>
                  <Badge colorScheme="blank" textWeight="light">
                    {translator("typography.addressUnknown")}
                  </Badge>
                </Box>
              );
            } else if (addressInfo.isNA) {
              return (
                <Box mx={20}>
                  <Badge colorScheme="blank" textWeight="light">
                    N/A
                  </Badge>
                </Box>
              );
            }
            return null;
          })()}
        </Stack>
      );
    },
    sortable: true,
    wrap: true
  },
  {
    name: translator("tableHeading.rideEnd", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: types.IObjectRide) => {
      const resolvedAddress = resolvedAddresses.find(
        (addr) =>
          addr.latitude === row.endLatitude &&
          addr.longitude === row.endLongitude
      );

      const addressToDisplay = resolvedAddress?.address;

      return (
        <Stack spacing={2}>
          {row.status === 1 ? (
            <Badge colorScheme={"purple"}>Ride in progress</Badge>
          ) : (
            <Fragment>
              <div>{utils.formatDate({ date: row?.endDate })}</div>
              {(() => {
                const addressInfo = getAddressDisplayInfo(
                  row.endLatitude,
                  row.endLongitude,
                  addressToDisplay
                );

                if (addressInfo.shouldShow) {
                  return (
                    <div>
                      {resolvedAddressLoading ? (
                        <>
                          <Skeleton variant="text" width={100} height={18} />
                          <Skeleton variant="text" width={100} height={18} />
                        </>
                      ) : (
                        addressToDisplay || "-"
                      )}
                    </div>
                  );
                } else if (addressInfo.isHidden) {
                  return (
                    <Box mx={20}>
                      <Badge
                        colorScheme="blank"
                        icon={<HiddenBadgeIcon />}
                        textWeight="light"
                      >
                        {translator("typography.addressIsHidden")}
                      </Badge>
                    </Box>
                  );
                } else if (addressInfo.isUnknown) {
                  return (
                    <Box mx={20}>
                      <Badge colorScheme="blank" textWeight="light">
                        {translator("typography.addressUnknown")}
                      </Badge>
                    </Box>
                  );
                } else if (addressInfo.isNA) {
                  return (
                    <Box mx={20}>
                      <Badge colorScheme="blank" textWeight="light">
                        N/A
                      </Badge>
                    </Box>
                  );
                }
                return null;
              })()}
            </Fragment>
          )}
        </Stack>
      );
    },
    sortable: true,
    wrap: true
  },
  {
    name: translator("tableHeading.tags", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: types.IObjectRide) => (
      <Stack spacing={2}>
        {row.status !== 1 ? (
          <>
            <Badge colorScheme={"info"} icon={<Clock />} textVariant={"small"}>
              {getObjectRideTotalTimeInMinutes(row)} {"min"}
            </Badge>
            <Badge
              colorScheme={"info"}
              icon={<MileageIcon />}
              textVariant={"small"}
            >
              {round(row.mileage || 0, 1)} {"km"}
            </Badge>
          </>
        ) : null}
      </Stack>
    ),
    sortable: true,
    width: "150px"
  },
  {
    name: translator("tableHeading.keyLabel", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: types.IObjectRide) => {
      // Compute once, keep logic simple and consistent
      // @ts-ignore
      const keyLabel = row?.key?.label ?? row?.keyId ?? row?.rawKeyId ?? "N/A";
      // @ts-ignore
      const keyId = row?.key?.keyId ?? row?.keyId ?? row?.rawKeyId;

      return (
        <Stack spacing={0.5} sx={{ pointerEvents: "none" }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              color: "#1D1F2B"
            }}
          >
            {keyLabel}
          </Typography>
          {keyId ? (
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: "400",
                color: "#646D7B"
              }}
            >
              ID {keyId}
            </Typography>
          ) : null}
        </Stack>
      );
    },
    sortable: true,
    width: "150px"
  },
  {
    name: translator("tableHeading.driver", {
      ns: "tableHeadingsTranslation"
    }),
    selector: (row: types.IObjectRide) => {
      // @ts-ignore
      const driverName: string | undefined = row?.driverName;
      // @ts-ignore
      const driver: any = row?.driver;
      const composed = driver
        ? `${driver?.firstName ?? ""} ${driver?.lastName ?? ""}`.trim()
        : "";
      const fallbackEmail = driver?.email ?? "";
      const name = driverName || composed || fallbackEmail;
      return name || "N/A";
    },
    sortable: true,
    width: "180px"
  }
];

export const IgnitionColorSchemes: any = {
  On: "info",
  Off: "error",
  null: "grey",
  "N/A": "grey"
};

export const IgnitionColorValues: any = {
  On: "#0269EC",
  Off: "#CC1010",
  null: "#959EB2",
  "N/A": "#959EB2"
};

//
// TAB PANES HEADINGS
//
export const OBJECTS_DETAILS_TABS_HEADINGS = (
  translator: any,
  features: IFeatures
): string[] =>
  compact([
    translator("common.details"),
    features.isRidesEnabled ? translator("common.rides") : null
  ]);

export const OBJECTS_DETAILS_TABS_INDEX_TO_ROUTENAME_MAPPING: Record<
  number,
  string
> = {
  0: "details",
  1: "rides"
};
